/**
 * Relative time, bytes, counts, percentages.
 *
 * Every one of these returns a string that goes through t() at the call site
 * when it carries a word, so a formatter never smuggles untranslated copy into
 * the UI.
 */

const MINUTE = 60_000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

export interface RelativeTime {
  /** A t() key, with a {n} placeholder unless the count is one. */
  key: string;
  n: number;
}

/**
 * Returns the key and the number, never a joined sentence.
 *
 * One gets its own key, the same way the traffic light's down sentence does.
 * "1 minutes ago" is a small thing that costs a large thing: it is the tell
 * that a screen is assembling sentences out of parts, on the one screen whose
 * entire job is to be believed. The singular keys carry no placeholder, which
 * is deliberate, because "a minute ago" is the sentence a person actually
 * says, and the call sites already pass n harmlessly.
 */
export function relativeTime(iso: string, now = Date.now()): RelativeTime | null {
  const at = new Date(iso).getTime();
  if (Number.isNaN(at)) return null;
  const diff = Math.max(0, now - at);
  if (diff < MINUTE) return { key: 'just now', n: 0 };
  if (diff < HOUR) {
    const n = Math.floor(diff / MINUTE);
    return { key: n === 1 ? 'a minute ago' : '{n} minutes ago', n };
  }
  if (diff < DAY) {
    const n = Math.floor(diff / HOUR);
    return { key: n === 1 ? 'an hour ago' : '{n} hours ago', n };
  }
  const n = Math.floor(diff / DAY);
  return { key: n === 1 ? 'yesterday' : '{n} days ago', n };
}

/** Duration in seconds, for build times. Mono tabular at the call site. */
export function durationSeconds(ms: number): string {
  return `${Math.round(ms / 1000)}s`;
}

const BYTE_UNITS = ['B', 'KB', 'MB', 'GB', 'TB'];

export function bytes(value: number): string {
  if (value <= 0) return '0 B';
  const power = Math.min(BYTE_UNITS.length - 1, Math.floor(Math.log(value) / Math.log(1024)));
  const scaled = value / 1024 ** power;
  const digits = scaled >= 100 || power === 0 ? 0 : 1;
  return `${scaled.toFixed(digits)} ${BYTE_UNITS[power]}`;
}

export function count(value: number): string {
  return new Intl.NumberFormat('en-US').format(value);
}

/**
 * The exact ratio, unrounded. It drives bar widths, aria-valuenow and the
 * tone thresholds, and it must not round: 99.6 percent of a limit rounding
 * up to 100 was a bar that said "at the limit" about a project the server
 * still calls `approaching`, which is the one disagreement this number is
 * not allowed to have.
 */
export function percent(used: number, limit: number): number {
  if (limit <= 0) return 0;
  return (used / limit) * 100;
}

/**
 * The percentage as a person reads it: two decimal places, always both.
 *
 * Two rather than none because the meter it labels is a monthly allowance
 * measured in abstract units, where a whole percent is thousands of units and
 * "0%" is the wrong answer for an app that has genuinely done work. A number
 * that moves when you use the product is a number that reads as measured.
 */
export function percentLabel(used: number, limit: number, digits = 2): string {
  return percent(used, limit).toFixed(digits);
}

/** Middle truncation, so both the subdomain and the TLD stay readable. */
export function middleTruncate(value: string, max = 44): string {
  if (value.length <= max) return value;
  const head = Math.ceil((max - 1) / 2);
  const tail = Math.floor((max - 1) / 2);
  return `${value.slice(0, head)}…${value.slice(value.length - tail)}`;
}

export function shortSha(sha: string): string {
  return sha.slice(0, 7);
}
